import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import PendingInvoiceDetailsSidebar
	from "modules/billing/components/PendingInvoicesIndexView/components/PendingInvoiceDetailsSidebar";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import Table from "~/components/Table";
import { INVOICES_PENDING_PAGE_KEY, INVOICES_SHARED_FILTER_VALUES } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import type { UnprocessedInvoicesIndexResponse } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import {
	applyDeliveryPeriodFilter,
	castFilterDatesToDate,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import InvoicePageHeader from "~/modules/billing/components/InvoicePageHeader";
import DownloadPendingInvoicesTableDataButton
	from "~/modules/billing/components/PendingInvoicesIndexView/components/DownloadPendingInvoicesTableDataButton";
import UpdatePendingInvoiceCommentSidebar
	from "~/modules/billing/components/PendingInvoicesIndexView/components/UpdatePendingInvoiceCommentSidebar";
import {
	pendingInvoicesIndexTableColumnDefs,
} from "~/modules/billing/components/PendingInvoicesIndexView/pendingInvoicesTableColumnDefs.tsx";
import type {
	InvoiceCommentData,
	UnprocessedInvoicesIndexTableData,
	UnprocessedInvoicesIndexTableMeta,
} from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import {
	createUnprocessedInvoiceTableDataFromProjectInvoice,
	InvoicePageTabId,
	invoiceTablesDefaultPageState,
	invoiceTableSharedFilterDefaultPageState,
} from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";

type Props = {
	isLoading: boolean,
	pendingInvoicesData?: UnprocessedInvoicesIndexResponse
};

const PendingInvoicesIndexView = ({ isLoading, pendingInvoicesData }: Props) => {
	const { hasAnyPermission } = useAuth();
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: INVOICES_PENDING_PAGE_KEY,
		defaultState: invoiceTablesDefaultPageState,
	});

	const { pageState: sharedFilterPageState, setPageState: setSharedFilterPageState } = usePageStateStorage({
		pageKey: INVOICES_SHARED_FILTER_VALUES,
		defaultState: invoiceTableSharedFilterDefaultPageState,
	});
	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRangeNullable>(castFilterDatesToDate(sharedFilterPageState.deliveryPeriodFilter));
	const [globalFilter, setGlobalFilter] = useState<string>(sharedFilterPageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState({ sorting });
	}, [setPageState, sorting]);

	useEffect(() => {
		setSharedFilterPageState({ globalFilter, deliveryPeriodFilter });
	}, [deliveryPeriodFilter, globalFilter, setSharedFilterPageState]);

	const [invoiceCommentToUpdate, setInvoiceCommentToUpdate] = useState<InvoiceCommentData | undefined>();
	const [invoiceIdToShowDetailsOf, setInvoiceIdToShowDetailsOf] = useState<string | undefined>();

	const tableData: UnprocessedInvoicesIndexTableData[] = useMemo(() => {
		if (pendingInvoicesData) {
			return pendingInvoicesData.projectInvoices.map((invoice) => {
				return createUnprocessedInvoiceTableDataFromProjectInvoice(invoice, pendingInvoicesData.projectPhases, pendingInvoicesData.projects);
			});
		}
		return [];
	}, [pendingInvoicesData]);

	const preFilteredTableData = useMemo(() => {
		return applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);
	}, [tableData, deliveryPeriodFilter]);

	const handleRowClick = useCallback((row: Row<UnprocessedInvoicesIndexTableData>) => {
		setInvoiceIdToShowDetailsOf(row.original.id);
	}, []);

	const handleUpdateCommentClick = useCallback((event: MouseEvent, row: Row<UnprocessedInvoicesIndexTableData>) => {
		event.stopPropagation();
		setInvoiceCommentToUpdate({
			invoiceId: row.original.id,
			comment: row.original.comment,
			isProjectInvoice: row.original.isProjectInvoice,
		});
	}, []);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: pendingInvoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
			onUpdateCommentClick: handleUpdateCommentClick,
		} as UnprocessedInvoicesIndexTableMeta,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	return <div className={clsx("auto-grid-2-rows mx-auto h-screen px-8")}>
		<InvoicePageHeader activeTabId={InvoicePageTabId.Pending}
						   deliveryPeriodFilter={deliveryPeriodFilter}
						   globalFilter={globalFilter}
						   setDeliveryPeriodFilter={setDeliveryPeriodFilter}
						   setGlobalFilter={setGlobalFilter}
						   userCanManageInvoices={userCanManageInvoices}
		>
			<DownloadPendingInvoicesTableDataButton deliveryPeriodFilter={deliveryPeriodFilter}
													filteredTableData={table.getFilteredRowModel().rows.map(row => row.original)}
													search={globalFilter} />
		</InvoicePageHeader>
		<div className="h-full space-y-8 overflow-y-auto pb-10">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true} />
		</div>
		<PendingInvoiceDetailsSidebar invoiceId={invoiceIdToShowDetailsOf}
									  isOpen={!!invoiceIdToShowDetailsOf}
									  onClose={() => setInvoiceIdToShowDetailsOf(undefined)}
									  userCanManageInvoices={userCanManageInvoices}
		/>
		<UpdatePendingInvoiceCommentSidebar isOpen={!!invoiceCommentToUpdate}
											onClose={() => setInvoiceCommentToUpdate(undefined)}
											invoiceCommentData={invoiceCommentToUpdate}
											userCanManageInvoices={userCanManageInvoices}
		/>
	</div>;
};

export default PendingInvoicesIndexView;